import { shared, stores } from '@kurtosys/app-start';
import { components, models as componentModels } from '@kurtosys/ksys-app-components';
import { common, query } from '@kurtosys/ksys-app-template';
import { IDocumentSearchClause } from '@kurtosys/types/api/document/IDocumentSearchClause.js';
import { ISort } from '@kurtosys/types/api/search/ISort.js';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { IConditional } from '@kurtosys/types/common/IConditional.js';
import { IExecutionOptions, IQueryContextEntity } from '@kurtosys/types/query/index.js';
import { IQueryOptions } from '@kurtosys/types/query/IQueryOptions.js';
import { action, computed, makeObservable, observable } from 'mobx';

import { IComponentStyles } from '../../../models/app/IComponentStyles';
import { IConfiguration } from '../../../models/app/IConfiguration.js';
import { TStoreContext } from '../../../models/app/TStoreContext.js';
import { AppStore } from '../../App/stores/AppStore.js';
import { ColumnSelectorStore } from '../../ColumnSelector/stores/ColumnSelectorStore.js';
import { ITableComponentProps } from '../models/ITableComponentProps.js';
import { ITableConfiguration } from '../models/ITableConfiguration.js';
import { IPerformanceRow } from '../models/ITablePerformanceRow.js';
import { ITablePropsConfig } from '../models/ITablePropsConfig.js';

type ITableRow = components.base.Table.models.ITableRow;
type ITableHeaderGroup = components.base.Table.models.ITableHeaderGroup;
type ITableColumn = components.base.Table.models.ITableColumn;
type ITableSort = components.base.Table.models.ITableSort;
type TSortButtonDirection = components.base.SortButton.models.SortButtonDirection;
type FetchAndQueryStore = stores.FetchAndQueryStore<IConfiguration, IComponentStyles>;
type IDocumentContextMenuData = components.base.Table.components.DocumentContextMenu.models.IDocumentContextMenuData;
type IDocumentContextMenuItem  = components.base.Table.components.DocumentContextMenu.models.IDocumentContextMenuItem;
type IDocumentContextMenuItemQueries = components.base.Table.components.DocumentContextMenu.models.IDocumentContextMenuItemQueries;
type IDocumentContextMenuProps = components.base.Table.components.DocumentContextMenu.models.IDocumentContextMenuProps;

const ConditionalHelper = common.helpers.ConditionalHelper;
const commonUtils = common.commonUtils;
const SortButtonDirection = components.base.SortButton.models.SortButtonDirection;
const TableColumnDisplayType = components.base.Table.models.TableColumnDisplayType;
const UnstyledTable = components.base.Table.UnstyledTable;

export class TableStore extends stores.base.StoreBase<IConfiguration, IComponentStyles> {
	static componentKey = 'table' as const;

	@observable
	sort: { column: string; direction: TSortButtonDirection } | undefined;

	@observable
	contextedTimestamp: number | undefined;

	existingDocumentsByClientCode: Set<string> = new Set<string>();

	@computed
	get queryStore() {
		return this.storeContext.get<stores.QueryStore<IConfiguration, IComponentStyles>>('queryStore');
	}

	@computed
	get appStore() {
		return this.storeContext.get<AppStore>('appStore');
	}

	@computed
	get columnSelectorStore() {
		return this.storeContext.get<ColumnSelectorStore>('columnSelectorStore');
	}

	constructor(storeContext: TStoreContext, manifest: IManifest) {
		super(storeContext, manifest);
		makeObservable(this);
	}

	@computed
	get configuration(): ITableConfiguration | undefined {
		return this.appStore?.getComponentConfiguration(TableStore.componentKey);
	}

	@computed
	get breakpoints(): componentModels.IBreakpointProps<ITableConfiguration>[] | undefined {
		return this.configuration?.breakpoints;
	}

	@computed
	get configurationWithoutBreakpoints(): ITableConfiguration | undefined {
		if (this.configuration) {
			const { breakpoints, ...config } = this.configuration;
			return config;
		}
	}

	@computed
	get activeConfiguration(): ITableConfiguration {
		const responsive = new shared.ResponsiveConfig<IConfiguration, IComponentStyles, ITableConfiguration>(
			this.storeContext,
			this.configurationWithoutBreakpoints,
			this.breakpoints,
		);
		return responsive.activeConfiguration;
	}

	@computed
	get dynamicRows() {
		return this.activeConfiguration?.dynamicRows;
	}

	@computed
	get dynamicRowsExecutionOptions(): Partial<IExecutionOptions> {
		return;
	}

	@computed
	get dynamicColumns() {
		return this.activeConfiguration?.dynamicColumns;
	}

	@computed
	get dynamicRowDocuments() {
		return this.activeConfiguration?.dynamicRowDocuments;
	}

	@computed
	get hasData(): boolean | undefined {
		const props = this.props;
		if (props) {
			if (common.commonUtils.isArrayLike(props)) {
				return (props as components.base.Table.models.ITableProps<IPerformanceRow>[]).some((prop) => {
					return this.rowsHasData(prop.rows, prop.columns);
				});
			}
			return props && this.rowsHasData(props.rows, props.columns);
		}
		return false;
	}

	@computed
	get dynamicRowExpandSource(): IQueryOptions | undefined {
		return this.activeConfiguration.dynamicRowExpandSource;
	}

	@action
	fetchChildren = async (row: ITableRow, columns: ITableColumn[]): Promise<ITableRow[]> => {
		const queryStore = this.storeContext.get<stores.QueryStore<IConfiguration, IComponentStyles>>('queryStore');
		const fetchAndQueryStore =
			this.storeContext.get<stores.FetchAndQueryStore<IConfiguration, IComponentStyles>>('fetchAndQueryStore');
		let result = [];
		if (this.dynamicRowExpandSource) {
			const query: IQueryOptions = this.dynamicRowExpandSource;
			const { queryId, queryOptionsType } = query;
			const options = {
				query,
				key: queryId,
				overridingExecutionOptions: {
					inputs: queryStore.inputs
				},
				responseContext: row,
			};
			if (queryOptionsType === 'dataset') {
				result = await fetchAndQueryStore.datasets(options);
			} else if (queryOptionsType === 'entities') {
				result = await fetchAndQueryStore.entities(options);
			}
			const docContextCol = columns.find((column) => column?.display?.type === TableColumnDisplayType.documentContextMenu &&
			column.display.options?.documentContextMenu);
			if (docContextCol) {
				this.orchestrateDocuments(result, docContextCol.display.options?.documentContextMenu);
			}
		}
		if (result.length > 0) {
			await this.fetchDocuments(result);
		}
		return result;
	};

	@action
	hideToggle = (row: ITableRow, conditional: IConditional): boolean => {
		const conditionalInstance = new ConditionalHelper({ queryClass: query.Query }, conditional);
		return conditionalInstance.matchesWithOptions({
			instance: row,
		});
	};

	@action
	disableToggle = (row: ITableRow, conditional: IConditional): boolean => {
		const conditionalInstance = new ConditionalHelper({ queryClass: query.Query }, conditional);
		return conditionalInstance.matchesWithOptions({
			instance: row,
		});
	};

	columnSortProperties: Record<string, string[]> = {};

	rowDocuments: {[key: string]: IDocumentContextMenuItem[]} = {};

	orchestrateDocuments = (rows: any[], props: IDocumentContextMenuProps) => {
		const { data, itemTemplate } = props;
		const documentsObject = rows.reduce((response, row) => {
			if (!row._rowKey) {
				row._rowKey = common.commonUtils.generateGuid();
			}
			response[row._rowKey] = this.getRowDocuments(row, data, itemTemplate);
			return response;
		}, {});
		this.rowDocuments = { ...this.rowDocuments, ...documentsObject};
	}

	private getRowDocuments = (
		rowData: Record<string, any>,
		dataConfig: IDocumentContextMenuData,
		itemConfig: IDocumentContextMenuItemQueries,
	): IDocumentContextMenuItem[] => {
		let documents = this.getQueryValue(dataConfig.source, rowData);

		if (common.commonUtils.isArrayLike(documents)) {
			if (dataConfig.conditional) {
				const conditional = new ConditionalHelper(
					{
						queryClass: query.Query,
					},
					{ conditions: [] },
				);
				documents = documents.filter((document) => {
					return conditional.matchesWithOptions({
						instance: document,
						executionOptions: {
							responseContext: rowData,
						},
						conditional: dataConfig.conditional,
					});
				});
			}
			const contextMenuItems: IDocumentContextMenuItem[] = documents.map((document) => {
				const { linkTarget, ...queries } = itemConfig;
				return {
					...this.mergeQueriesAndProps({ queries }, document) as object,
					linkTarget,
				  };
			});
			return contextMenuItems;
		}
		return [];
	};

	@action
	getDocumentsCallback = (rowData: Record<string, any>): IDocumentContextMenuItem[] => {
		return this.rowDocuments[rowData._rowKey] || [];
	};

	@computed
	get orchestratedProps(): components.base.Table.models.ITableProps<IPerformanceRow>[] {
		if (this.contextedTimestamp === undefined || !this.appStore || !this.queryStore) {
			return [];
		}

		const config = this.activeConfiguration;
		let props: ITablePropsConfig<IPerformanceRow> = {
			queryStore: this.queryStore,
			appParamsInputs: this.appStore.appParamsHelper.inputs,
			...this.mergeQueriesAndProps(config),
		};
		if (!props.sort) {
			props.sort = {};
		}
		if (!props.sort.mode) {
			props.sort.mode = this.sortMode;
		}
		let multiTableDataSet: components.base.Table.models.ITableProps<IPerformanceRow>[] = [];

		// Get dynamic values
		if (config) {
			// First Sort out the columns, then it will be available if multi dataset
			if (this.dynamicColumns && this.dynamicColumns.query) {
				const { type = 'append', query } = this.dynamicColumns;
				const columns = this.getQueryValue(query) as ITableColumn[];
				if (type === 'append') {
					props.columns = [...(props.columns || []), ...columns];
				}
				if (type === 'replace') {
					props.columns = columns;
				}
			}

			if (this.dynamicRows) {
				const rows = this.queryStore.query(this.dynamicRows, this.dynamicRowsExecutionOptions);
				if (common.commonUtils.isArrayLike(rows)) {
					props.rows = rows;
				}
				if (this.isMultiTableDataSet(rows)) {
					const keys = Object.keys(rows);
					for (const key of keys) {
						const newProps = { ...props };
						newProps.rows = rows[key];
						// Resolve the table header values based on config key
						const columns = newProps.columns;
						const headerGroups = newProps.headerGroups;
						if (columns && headerGroups) {
							const headerColumnConfig = columns.find((col) => !!col.headerGroupId);
							if (headerColumnConfig) {
								const headerGroupConfig: ITableHeaderGroup[] = headerGroups.map((group) => {
									if (group.id === headerColumnConfig.headerGroupId) {
										group = { ...group, header: key };
									}
									return group;
								});
								newProps.headerGroups = headerGroupConfig;
							}
						}
						multiTableDataSet.push({ ...newProps });
					}
				}
			}


			props.columns = props.columns.map((column) => {
				if (
					column?.display?.type === TableColumnDisplayType.documentContextMenu &&
					column.display.options?.documentContextMenu
				) {
					this.orchestrateDocuments(props?.rows, column.display.options?.documentContextMenu);
					return {
						...column,
						display: {
							...column.display,
							options: {
								documentContextMenu: {
									...column.display.options.documentContextMenu,
									getDocumentsCallback: this.getDocumentsCallback,
								},
							},
						},
					};
				}
				return column;
			});

			if (this.columnSelectorStore.columnsMap) {
				// Filter out columns based on column selector dropdown selected items
				props.columns = props.columns.filter((item) => {
					const columnKey = this.columnSelectorStore.columnsMap.get(item.key);
					if (columnKey) {
						return columnKey.selected;
					}
					// If column selector is configured but column is not found in the map, then return the column
					return true;
				});
			}

			// the following logic is used to hide rows that don't have data, regardless if it comes from rows or dynamicRows config.
			if (this.appStore.appComponentConfiguration?.noDataOptions) {
				const { hideRow = false } = this.appStore.appComponentConfiguration.noDataOptions;
				if (hideRow) {
					if (!common.commonUtils.isNullOrEmpty(multiTableDataSet)) {
						multiTableDataSet = multiTableDataSet.map((data) => {
							return this.noDataCheck(data);
						});
					} else {
						props = this.noDataCheck(props);
					}
				}
			}
			if (this.appStore.splitTableOptions) {
				const { mustLimitRows, limit, start, hideDisclaimer } = this.appStore.splitTableOptions;
				if (mustLimitRows) {
					if (!common.commonUtils.isNullOrEmpty(multiTableDataSet)) {
						multiTableDataSet = multiTableDataSet.map((data) => {
							data.rows = (data.rows || []).slice(start, start + limit);
							return data;
						});
					} else {
						props.rows = (props.rows || []).slice(start, start + limit);
					}
				}
				if (hideDisclaimer) {
					if (!common.commonUtils.isNullOrEmpty(multiTableDataSet)) {
						multiTableDataSet = multiTableDataSet.map((data) => {
							data.disclaimersProps = undefined;
							return data;
						});
					} else {
						props.disclaimersProps = undefined;
					}
				}
			}
			if (this.sort && props.rows) {
				const { column, direction } = this.sort;
				props.rows = common.commonUtils.sortBy(
					props.rows,
					(row) => (row as any)[column],
					direction === SortButtonDirection.ascending ? 'ASC' : 'DESC',
					this.appStore.culture,
				);
			}
			if (props.columns) {
				props.columns.forEach((column) => {
					if (column.sort && column.sort.enabled) {
						const columnSortProperties = column.sort.propertyKeys ? column.sort.propertyKeys : [column.key];
						this.columnSortProperties[column.key] = columnSortProperties;
						column.sort = {
							...column.sort,
							onChange: this.sortOnChange,
						};
						if (this.currentSortAsCollection.length > 0) {
							const initialSort = this.currentSortAsCollection.find((s) => {
								return columnSortProperties.includes(s.key);
							});
							if (initialSort) {
								column.sort.direction =
									initialSort.direction === 'ASC'
										? SortButtonDirection.ascending
										: SortButtonDirection.descending;
							}
						}
					}
				});
			}
		}
		const workingProps = multiTableDataSet.length > 0 ? multiTableDataSet : [props];
		if (workingProps && common.commonUtils.isArrayLike(workingProps) && workingProps.length === 1) {
			const appKey = this.appStore.appKey;
			UnstyledTable.loadSuperscripts(
				appKey,
				workingProps[0].columns || [],
				workingProps[0].rows || [],
				workingProps[0].headerGroups || [],
				this.queryStore,
			);
		}
		return workingProps;
	}

	@computed
	get props(): ITableComponentProps | undefined {
		return this.orchestratedProps;
	}

	async fetchDocuments(rows: any) {
		const fetchAndQueryStore = this.storeContext.get<FetchAndQueryStore>('fetchAndQueryStore');

		const dynamicRowDocuments = this.dynamicRowDocuments;

		if (dynamicRowDocuments && rows && this.hasData) {
			const { columnKey, documentMetaKey, documentSearchRequest, documentPropertyKey } = dynamicRowDocuments;
			const searchValues = common.commonUtils.isArrayLike(rows)
				? rows.filter((row) => row[columnKey]).map((row) => row[columnKey])
				: [];

			if (searchValues.length > 0) {
				let search: IDocumentSearchClause[] = [];

				if (documentPropertyKey) {
					search = [
						{
							matchtype: 'MATCH',
							values: searchValues,
							property: documentPropertyKey,
							meta: false,
						},
					];
				}

				if (documentMetaKey) {
					search = [
						{
							matchtype: 'MATCH',
							values: searchValues,
							property: documentMetaKey,
						},
					];
				}

				if (documentSearchRequest.search.length > 0) {
					search = search.concat(documentSearchRequest.search);
				}

				if (search.length > 0) {
					const body = {
						...documentSearchRequest,
						search,
					};

					await fetchAndQueryStore.documents({
						requestBody: body,
						existingDocumentsByClientCode: this.existingDocumentsByClientCode,
					});
				}
			}
		}
	}

	@action
	async initialize(callbacks?: Record<string, EventListener>): Promise<void> {
		// TODO: implement
		this.contextedTimestamp = new Date().getTime();
		await this.fetchDocuments(this.getQueryValue(this.dynamicRows));
	}

	isBreakpointProps(
		props: ITableComponentProps | componentModels.IBreakpointProps<ITableComponentProps>[] | undefined,
	): props is componentModels.IBreakpointProps<ITableComponentProps>[] {
		return !!(
			props &&
			Array.isArray(props) &&
			(props[0] as componentModels.IBreakpointProps<ITableComponentProps>).modifier &&
			(props[0] as componentModels.IBreakpointProps<ITableComponentProps>).size
		);
	}

	isMultiTableDataSet(data: {
		[property: string]: IPerformanceRow[];
	}): data is { [property: string]: IPerformanceRow[] } {
		return typeof data === 'object' && Object.keys(data).every((key: string) => data[key].length > 0);
	}

	@computed
	get dynamicRowsDataContextConfig(): [string, IQueryContextEntity] {
		if (this.dynamicRows && this.dynamicRows.queryOptionsType === 'entities' && this.dynamicRows.queryId) {
			const contextKey = this.dynamicRows.contextKey;
			const context = this.queryStore.getContext({ contextKey });
			const { contextConfiguration } = context;
			if (contextConfiguration) {
				const entityRequest = contextConfiguration.entities?.find(
					(entity) => entity.id === this.dynamicRows.queryId,
				);
				return [contextKey, entityRequest];
			}
		}
		return [undefined, undefined];
	}

	/**
	 * Sort based on current data context
	 *
	 * @readonly
	 */
	get currentSort(): ISort | ISort[] | undefined {
		const [contextKey, entityRequest] = this.dynamicRowsDataContextConfig;
		return this.orchestrateSortFromEntitiesConfig(entityRequest);
	}

	get currentSortAsCollection(): ISort[] {
		let currentSort = [];
		if (this.currentSort) {
			currentSort = !common.commonUtils.isArrayLike(this.currentSort) ? [this.currentSort] : this.currentSort;
		}
		return currentSort;
	}

	private cachedSortMode: ITableSort['mode'];
	/**
	 * Get the fixed sort mode from configuration, otherwise infer it from the data context request
	 *
	 * @readonly
	 */
	get sortMode(): ITableSort['mode'] {
		if (!this.cachedSortMode) {
			// If any of the column sort options use multiple properties, multi sorting is required.
			const keysForColumnSortProperties = Object.keys(this.columnSortProperties);
			if (keysForColumnSortProperties.length > 0) {
				this.cachedSortMode = keysForColumnSortProperties.reduce((mode: ITableSort['mode'], key) => {
					if (this.columnSortProperties[key].length > 1) {
						return 'multiple';
					}
					return mode;
				}, 'single') as ITableSort['mode'];
			}
			// Otherwise if we have configured the API with sort options infer those
			else if (this.currentSort) {
				this.cachedSortMode = common.commonUtils.isArrayLike(this.currentSort) ? 'multiple' : 'single';
			}
			// fallback to the mode configured
			else if (this.activeConfiguration?.props?.sort?.mode) {
				this.cachedSortMode = this.activeConfiguration?.props?.sort?.mode;
			}
			// If all else fails just sort by single columns
			else {
				this.cachedSortMode = 'single';
			}
		}
		return this.cachedSortMode;
	}

	orchestrateSortFromEntitiesConfig(entity: IQueryContextEntity): ISort | ISort[] | undefined {
		let sort: ISort | ISort[] | undefined = entity?.requestBody?.sort;
		if (entity?.requestBodyQueries?.sort) {
			if (common.commonUtils.isArrayLike(entity.requestBodyQueries.sort)) {
				sort = entity.requestBodyQueries?.sort.map((s) => {
					const propertyKeyQuery = new query.Query(s.key);
					return {
						key: propertyKeyQuery.execute(this.queryStore.executionOptions),
						direction: s.direction,
					};
				});
			} else {
				const propertyKeyQuery = new query.Query(entity.requestBodyQueries.sort.key);
				sort = {
					key: propertyKeyQuery.execute(this.queryStore.executionOptions),
					direction: entity.requestBodyQueries.sort.direction,
				};
			}
		}
		return sort;
	}

	@action
	sortOnChange = async (payload: any, direction: TSortButtonDirection) => {
		if (payload.direction === 'unset') {
			this.sort = undefined;
		} else {
			this.sort = { direction, column: payload.key };
		}
		// API Sorting is only applicable when using API Pagination
		if (this.appStore.paginationStore) {
			const sortProperties = this.columnSortProperties[payload.key];
			const [contextKey, entityRequest] = this.dynamicRowsDataContextConfig;
			if (entityRequest) {
				let newSort;
				if (direction !== 'unset') {
					const newDirection = direction === 'ascending' ? 'ASC' : 'DESC';
					if (this.sortMode === 'multiple') {
						newSort = this.currentSortAsCollection.map((s) => {
							return {
								key: s.key,
								direction: sortProperties.includes(s.key) ? newDirection : s.direction,
							};
						});
						if (!newSort.find((s) => sortProperties.includes(s.key))) {
							const newPropertySorts = sortProperties.map((propertyKey) => {
								return {
									key: propertyKey,
									direction: newDirection,
								};
							});
							newSort.push(...newPropertySorts);
						}
					} else {
						newSort = {
							key: payload.key,
							direction: newDirection,
						};
					}
				} else {
					if (this.sortMode === 'multiple') {
						newSort = this.currentSortAsCollection.filter((s) => !sortProperties.includes(s.key));
						if (newSort.length <= 0) {
							newSort = undefined;
						}
					} else {
						newSort = undefined;
					}
				}
				this.appStore.paginationStore.reset();

				const newRequest = {
					...entityRequest,
				};

				if (newSort) {
					if (newRequest.requestBodyQueries) {
						newRequest.requestBodyQueries = {
							...newRequest.requestBodyQueries,
							sort: {
								key: {
									queryOptionsType: 'none',
									value: newSort.key,
								},
								direction: newSort.direction,
							},
						};
					}

					if (newRequest.requestBody) {
						newRequest.requestBody = {
							...newRequest.requestBody,
							sort: newSort,
						};
					}
				}

				this.queryStore.updateContextConfiguration(
					contextKey,
					{
						entities: [newRequest],
					},
					{
						arrayMergeStrategyByKey: {
							sort: 'DeepCopy',
						},
						arrayMergeStrategy: 'DeepMergeByKeyOrAppend',
						getKeyFn: (item) => {
							if (item.id) {
								return item.id;
							}
							if (item.key) {
								return item.key;
							}
							if (item.queryId) {
								return item.queryId;
							}
							return item;
						},
					},
				);
			}
		}
	};

	noDataCheck(props: components.base.Table.models.ITableProps<IPerformanceRow>) {
		const { rows, columns } = props;
		if (rows !== undefined && common.commonUtils.isArrayLike(rows) && columns !== undefined) {
			props.rows = rows.filter((row: IPerformanceRow) => {
				return !this.rowHasNoData(row, { columnConfig: columns });
			});
		}
		return props;
	}

	rowsHasData(rows: IPerformanceRow[] | undefined, columns: ITableColumn[] | undefined): boolean {
		if (Array.isArray(rows) && rows.length > 0) {
			const columnConfig = columns;
			if (columnConfig) {
				const hasNoData = rows.map((row) => {
					return this.rowHasNoData(row, { columnConfig });
				});
				return !hasNoData.every((row) => row);
			}
		}
		return false;
	}

	rowHasNoData(row: IPerformanceRow, options: { columnConfig: ITableColumn[] }): boolean {
		return Object.keys(row).every((columnKey) => {
			const column = options.columnConfig.find((col) => col.key === columnKey);
			return !column || column.excludeFromNoDataCheck || row[columnKey] === undefined || row[columnKey] === null;
		});
	}
}
